/**
 * Kayit / giris ekrani.
 *
 * Oturum yoksa (veya oturum gecersizse) TUM uygulamanin ONUNE gecer -
 * bkz. App.tsx auth kapisi. Referans kodu olmadan kayit YOKTUR; bu
 * kural burada erken geri bildirim icindir, GERCEK zorlama backend'de
 * (bkz. auth/store.py register_user).
 */

import { useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";
import type { AuthUser } from "../api/types";

type Mode = "login" | "register";

export function AuthScreen({ onAuthenticated }: { onAuthenticated: (user: AuthUser) => void }) {
  const [mode, setMode] = useState<Mode>("login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user =
        mode === "login"
          ? await api.auth.login(email.trim(), password)
          : await api.auth.register({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              phone: phone.trim(),
              email: email.trim(),
              password,
              referralCode: referralCode.trim().toUpperCase(),
            });
      onAuthenticated(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sunucuya ulaşılamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">
          <img
            className="brand-logo"
            src="./logo.png?v=3"
            alt=""
            aria-hidden="true"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
          <div className="brand-text">
            <span className="brand-name">Sinyalix</span>
            <span className="brand-sub">Sinyal Tarama · Spot</span>
          </div>
        </div>

        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={mode === "login" ? "auth-tab active" : "auth-tab"}
            onClick={() => switchMode("login")}
          >
            Giriş Yap
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={mode === "register" ? "auth-tab active" : "auth-tab"}
            onClick={() => switchMode("register")}
          >
            Kayıt Ol
          </button>
        </div>

        {error ? <div className="warn-banner error-banner">{error}</div> : null}

        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          {mode === "register" ? (
            <>
              <div className="auth-row">
                <input
                  className="input"
                  placeholder="Ad"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  autoComplete="given-name"
                  required
                />
                <input
                  className="input"
                  placeholder="Soyad"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
              <input
                className="input"
                type="tel"
                placeholder="Telefon"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                autoComplete="tel"
                required
              />
            </>
          ) : null}

          <input
            className="input"
            type="email"
            placeholder="E-posta"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={mode === "register" ? 8 : undefined}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            required
          />

          {mode === "register" ? (
            <input
              className="input"
              placeholder="Referans Kodu (SNLX-XXXXXXXX)"
              value={referralCode}
              onChange={(event) => setReferralCode(event.target.value)}
              required
            />
          ) : null}

          <button type="submit" className="btn btn-primary auth-submit" disabled={busy}>
            {busy ? "…" : mode === "login" ? "Giriş Yap" : "Kayıt Ol"}
          </button>
        </form>

        {mode === "register" ? (
          <p className="note auth-note">
            Referans kodunuz yoksa hesap oluşturamazsınız — yöneticinizden isteyin.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Oturum GEÇERLİ ama üyelik ACTIVE değil (EXPIRED/SUSPENDED) - panele girilemez. */
export function MembershipBlockedScreen({
  user,
  onLogout,
  busy,
}: {
  user: AuthUser;
  onLogout: () => void;
  busy: boolean;
}) {
  const message =
    user.status === "SUSPENDED"
      ? "Hesabınız askıya alınmış."
      : "Üyeliğiniz sona ermiştir.";

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">
          <div className="brand-text">
            <span className="brand-name">Sinyalix</span>
            <span className="brand-sub">Sinyal Tarama · Spot</span>
          </div>
        </div>
        <div className="warn-banner error-banner">{message}</div>
        <p className="note">
          {user.first_name} {user.last_name} ({user.email})
        </p>
        <button type="button" className="btn btn-primary auth-submit" onClick={onLogout} disabled={busy}>
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}


/**
 * Admin sifreyi sifirladiginda (`must_change_password`) gosterilen
 * ZORUNLU ekran - uygulamanin geri kalani buradan gecilmeden acilmaz.
 *
 * Yalnizca gorunurluk katmani degildir: backend de ayni bayrak varken
 * /api/auth disindaki her uc noktaya 403 doner (bkz. api/app.py
 * `_PASSWORD_CHANGE_ALLOWED`), yani bu ekran atlanarak API'ye dogrudan
 * istek atilamaz.
 */
export function ForcedPasswordChangeScreen({
  user,
  onChanged,
  onLogout,
  busy,
}: {
  user: AuthUser;
  onChanged: (user: AuthUser) => void;
  onLogout: () => void;
  busy: boolean;
}) {
  const [temporary, setTemporary] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError("Yeni şifre en az 8 karakter olmalı.");
      return;
    }
    if (next !== repeat) {
      setError("Yeni şifreler birbiriyle uyuşmuyor.");
      return;
    }
    setSubmitting(true);
    try {
      onChanged(await api.auth.changePassword(temporary, next));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Şifre değiştirilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">
          <div className="brand-text">
            <span className="brand-name">Sinyalix</span>
            <span className="brand-sub">Sinyal Tarama · Spot</span>
          </div>
        </div>
        <div className="warn-banner">
          Şifreniz yönetici tarafından sıfırlandı. Devam etmek için kendi şifrenizi belirleyin.
        </div>
        <p className="note">
          {user.first_name} {user.last_name} ({user.email})
        </p>
        <form onSubmit={(event) => void submit(event)} className="auth-form">
          <input
            type="password"
            className="auth-input"
            placeholder="Size iletilen geçici şifre"
            autoComplete="current-password"
            value={temporary}
            onChange={(event) => setTemporary(event.target.value)}
            required
          />
          <input
            type="password"
            className="auth-input"
            placeholder="Yeni şifre (en az 8 karakter)"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            required
          />
          <input
            type="password"
            className="auth-input"
            placeholder="Yeni şifre (tekrar)"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            required
          />
          {error ? <div className="warn-banner error-banner">{error}</div> : null}
          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={submitting || busy}
          >
            {submitting ? "Kaydediliyor…" : "Şifremi Belirle"}
          </button>
        </form>
        <button type="button" className="btn auth-submit" onClick={onLogout} disabled={busy}>
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}
